import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} SccPriceCompareRefs
 * @property {HTMLElement} sccPriceRows
 */

/**
 * Custom element for the SCC one-time / subscription price comparison block.
 * Mirrors the theme's native `product-price` component: on `variant:update`
 * it swaps in the section's freshly server-rendered markup for this same
 * `data-block-id`, so the one-time and subscription figures always match the
 * variant (size/grind) the shopper currently has selected.
 *
 * @extends {Component<SccPriceCompareRefs>}
 */
class SccPriceCompare extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.scheduleSubscriptionPrice);
    this.scheduleSubscriptionPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.scheduleSubscriptionPrice);
  }

  // Carry an explicit shopper choice across this product's existing variant cards.
  // Only the exact available plan is retained; the official widget owns form inputs.
  rememberPurchaseChoice = (event) => {
    const input = event?.target;
    if (!event?.isTrusted || !(input instanceof HTMLInputElement) || !input.checked) return;
    const widget = input.closest('.shopify_subscriptions_app_container');
    if (!widget || widget.dataset.productId !== this.dataset.productId) return;
    const type = input.dataset.radioType;
    if (type !== 'one_time_purchase' && type !== 'selling_plan') return;
    const planId = input.dataset.sellingPlanId;
    if (type === 'selling_plan' && !planId) return;
    for (const card of widget.querySelectorAll('section[data-variant-id]')) {
      if (card.contains(input)) continue;
      const options = Array.from(card.querySelectorAll('input[data-radio-type]'));
      const match = options.find((option) => option.dataset.radioType === type &&
        (type === 'one_time_purchase' || option.dataset.sellingPlanId === planId));
      // Never substitute another recurring plan when the chosen plan is unavailable.
      const choice = match || options.find((option) => option.dataset.radioType === 'one_time_purchase');
      if (choice) choice.checked = true;
    }
  };

  scheduleSubscriptionPrice = (event) => {
    this.rememberPurchaseChoice(event);
    queueMicrotask(() => { if (this.isConnected) this.syncSubscriptionPrice(); });
  };

  syncSubscriptionPrice = () => {
    const section = this.closest('.shopify-section, dialog');
    const rows = this.querySelector('[ref="sccPriceRows"]');
    if (!section || !rows) return;
    let row = rows.querySelector('[data-scc-selected-subscription-price]');
    if (row) { row.hidden = true; row.style.display = 'none'; }
    const widget = Array.from(section.querySelectorAll('.shopify_subscriptions_app_container'))
      .find((node) => node.getAttribute('data-product-id') === this.dataset.productId);
    if (!widget) return;
    const form = Array.from(section.querySelectorAll('form[action*="/cart/add"]'))
      .find((node) => node.elements['product-id']?.value === this.dataset.productId);
    const variantId = form?.querySelector('input[name="id"], select[name="id"]')?.value;
    if (!variantId) return;
    const selected = widget.querySelector('section[data-variant-id="' + variantId + '"] input[data-radio-type]:checked');
    if (!selected || !['selling_plan', 'selling_plan_group'].includes(selected.dataset.radioType) || !selected.dataset.sellingPlanId || !selected.dataset.variantPrice) return;
    if (!row) {
      row = document.createElement('div');
      row.className = 'scc-price-compare__row';
      row.setAttribute('data-scc-selected-subscription-price', '');
      const label = document.createElement('span');
      label.className = 'scc-price-compare__label';
      label.textContent = 'Subscription per delivery';
      const value = document.createElement('span');
      value.className = 'scc-price-compare__value';
      row.append(label, value);
      rows.appendChild(row);
    }
    row.querySelector('.scc-price-compare__value').textContent = selected.dataset.variantPrice;
    row.hidden = false;
    row.style.display = '';
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { sccPriceRows } = this.refs;
    const newBlock = event.detail.data.html.querySelector(`scc-price-compare[data-block-id="${this.dataset.blockId}"]`);
    if (!newBlock) return;

    const newRows = newBlock.querySelector('[ref="sccPriceRows"]');
    if (newRows && sccPriceRows) {
      sccPriceRows.replaceWith(newRows);
    }

    const newNotes = newBlock.querySelectorAll('.scc-price-compare__note');
    const oldNotes = this.querySelectorAll('.scc-price-compare__note');
    oldNotes.forEach((node) => node.remove());
    newNotes.forEach((node) => this.appendChild(node.cloneNode(true)));
    this.scheduleSubscriptionPrice();
  };
}

if (!customElements.get('scc-price-compare')) {
  customElements.define('scc-price-compare', SccPriceCompare);
}
